import React, { FC, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

import LegalLoading from '~/projects/polyllm-chat/components/LegalPage/components/LegalLoading';

// CookieYes ships a light-themed stylesheet for its audit tables (white cells,
// dark text). Override it with theme-aware colors so the tables match both the
// light and dark app themes. The MUI-generated class prefix gives these rules
// higher specificity than CookieYes' own selectors, so no `!important` needed.
const cookiePolicyStyles = {
  '& .cky-banner-element': {
    backgroundColor: 'action.selected',
    color: 'text.primary',
    borderColor: 'divider',
  },
  '& .cky-cookie-audit-table th': {
    backgroundColor: 'action.selected',
    color: 'text.primary',
    borderColor: 'divider',
  },
  '& .cky-cookie-audit-table td': {
    color: 'text.secondary',
    borderColor: 'divider',
  },
  '& .cky-cookie-audit-table tr:nth-of-type(2n + 1) td': {
    backgroundColor: 'action.hover',
  },
  '& .cky-cookie-audit-table tr:nth-of-type(2n) td': {
    backgroundColor: 'transparent',
  },
};

export const COOKIE_YES_POLICY_SCRIPT_ID = 'cky-cookie-policy';
export const COOKIE_YES_POLICY_CONTAINER_ID = 'cky-policy-container';
export const COOKIE_YES_POLICY_STYLE_ID = 'cky-policy-style';
export const COOKIE_YES_POLICY_SCRIPT_SRC =
  'https://cdn-cookieyes.com/client_data/7e896f2807ab5d0d9993a22a73fca0e7/cookie-policy/script.js';

const CookieYesEmbed: FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    // CookieYes injects the policy (`#cky-policy-container`) right after its
    // own <script>. Reveal the content only once it actually appears — the
    // script's `onload` fires before the policy is fetched and rendered.
    const observer = new MutationObserver(() => {
      if (container.querySelector(`#${COOKIE_YES_POLICY_CONTAINER_ID}`)) {
        setIsLoaded(true);
      }
    });
    observer.observe(container, { childList: true, subtree: true });

    const script = document.createElement('script');
    script.id = COOKIE_YES_POLICY_SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = COOKIE_YES_POLICY_SCRIPT_SRC;
    // The CookieYes script only renders on the window `load` event. In an SPA
    // that event has already fired by the time we inject the script, so it
    // would never render. Re-dispatch `load` to trigger it. When the page is
    // still loading, the real `load` event will run the handler on its own.
    script.onload = (): void => {
      if (
        document.readyState === 'complete' &&
        !document.getElementById(COOKIE_YES_POLICY_CONTAINER_ID)
      ) {
        window.dispatchEvent(new Event('load'));
      }
    };
    container.appendChild(script);

    return (): void => {
      observer.disconnect();
      script.remove();
      document.getElementById(COOKIE_YES_POLICY_CONTAINER_ID)?.remove();
      document.getElementById(COOKIE_YES_POLICY_STYLE_ID)?.remove();
    };
  }, []);

  return (
    <>
      {!isLoaded && <LegalLoading />}
      <Box ref={containerRef} sx={cookiePolicyStyles} />
    </>
  );
};

export default CookieYesEmbed;
